import React, { useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { Redirect, useHistory } from "react-router-dom";
import MessageTip from "../MessageTip";
import { STATUS } from "../../../constants";
import useTimer from "../useTimer";
import useRecaptcha from "../useRecaptcha";
import RecaptchaLoadTip from "../RecaptchaLoadTip";
import { isRecaptchaCredentialError } from "../recaptchaUtils";
import { setMfaSession } from "../../../utils/mfaSession";
import { reportRecaptchaAuthError } from "../../../infra/sentry";
import AuthPageLayout from "../AuthPageLayout";

const { DEFAULT, SUCCEED, FAILED, LOADING } = STATUS;
const VERIFY_RESULT_MAPPING = {
  [STATUS.SUCCEED]: {
    className: "is-success",
    message: "Sign In Successfully!",
  },
  [STATUS.FAILED]: {
    className: "is-danger",
    message: "Failed to sign in.",
  },
};

const MFAVerify = () => {
  const history = useHistory();
  const [smsSendStatus, setSmsSendStatus] = useState({
    status: DEFAULT,
  });
  const [verifyStatus, setVerifyStatus] = useState({
    status: DEFAULT,
  });
  const [verificationId, setVerificationId] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [sendBtnDisabled, setSendBtnDisabled] = useState(false);
  const { startTimer, countdown } = useTimer();

  const {
    recaptchaVerifier,
    isRecaptchaReady,
    isRecaptchaLoading,
    hasRecaptchaLoadFailed,
    resetRecaptcha,
    resetRecaptchaWidget,
  } = useRecaptcha({
    containerId: "recaptcha-container",
  });

  if (!window.resolver) {
    return <Redirect to="/" />;
  }

  const handleSendVerifyCode = async () => {
    if (smsSendStatus.status === LOADING || !isRecaptchaReady || !recaptchaVerifier) {
      return;
    }

    setSmsSendStatus({
      status: LOADING,
    });

    try {
      const phoneInfoOptions = {
        multiFactorHint: window.resolver.hints[0],
        session: window.resolver.session,
      };

      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
      setVerificationId(verificationId);
      setSmsSendStatus({
        status: SUCCEED,
      });
      setSendBtnDisabled(true);
      startTimer({ second: 20, onTimeout: () => setSendBtnDisabled(false) });
    } catch (e) {
      reportRecaptchaAuthError(e, { phase: "send-sms", containerId: "recaptcha-container", flow: "mfa-verify" });
      setVerificationId(null);
      if (isRecaptchaCredentialError(e)) {
        setSmsSendStatus({
          status: FAILED,
          error: "Your session or security check expired. We have reset it — please try sending the code again.",
        });
        await resetRecaptchaWidget();
        return;
      }
      if (e.code === "auth/invalid-multi-factor-session") {
        setSmsSendStatus({
          status: FAILED,
          error: `Session expired, please refresh your browser.`,
        });
      } else if (e.code === "auth/too-many-requests") {
        setSmsSendStatus({
          status: FAILED,
          error: `Sorry, too many attempts, please try again later.`,
        });
      } else {
        setSmsSendStatus({
          status: FAILED,
          error: `Failed to send verify code to your phone${e.message ? `, the error might be ${e.message}` : ""}`,
        });
      }
      await resetRecaptcha();
    }
  };

  const handleVerify = async () => {
    if (verifyStatus.status === LOADING) {
      return;
    }

    if (!verificationCode) {
      setVerifyStatus({
        status: FAILED,
        error: "Please input your verify code",
      });
      return;
    }

    setVerifyStatus({
      status: LOADING,
    });

    try {
      const cred = firebase.auth.PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(cred);

      const { user } = await window.resolver.resolveSignIn(multiFactorAssertion);
      // Recorded before navigating away, because App validates the session as
      // soon as it mounts, and the uid has to match the signed in user or it
      // will bounce straight back out. It also gives this flow the same
      // absolute expiry as the new one, instead of a Firebase credential that
      // never goes stale.
      const signedInUser = user || firebase.auth().currentUser;
      setMfaSession({
        sessionId: verificationId,
        loginEmail: signedInUser?.email,
        firebaseAuthUid: signedInUser?.uid,
      });
      setVerifyStatus({
        status: SUCCEED,
      });
    } catch (e) {
      reportRecaptchaAuthError(e, { phase: "verify-code", flow: "mfa-verify" });
      if (isRecaptchaCredentialError(e)) {
        setVerifyStatus({
          status: FAILED,
          error: "Your verification code expired. Please send a new code and try again.",
        });
        await resetRecaptchaWidget();
        return;
      }
      setVerifyStatus({
        status: FAILED,
        error: `Failed to login, your phone verify failed${e.message ? `, the error might be ${e.message}` : ""}`,
      });
    } finally {
      history.push("/");
    }
  };

  return (
    <AuthPageLayout title="Multi-factor authentication" subtitle="Verify your identity to continue">
      <form className="public-secure-file-form" onSubmit={(event) => event.preventDefault()}>
        <div className="public-secure-file-notice">
          <i className="fas fa-message" aria-hidden="true" />
          <p>We will send a verification code to your enrolled mobile number via SMS.</p>
        </div>
        <div id="recaptcha-container" style={{ width: "max-content", margin: "0 auto 20px" }} />
        <RecaptchaLoadTip
          isRecaptchaLoading={isRecaptchaLoading}
          hasRecaptchaLoadFailed={hasRecaptchaLoadFailed}
          onRetry={resetRecaptcha}
        />
        <button
          disabled={!isRecaptchaReady || sendBtnDisabled}
          className={`public-secure-file-button public-secure-file-button--primary ${
            smsSendStatus.status === LOADING ? "is-loading" : ""
          }`}
          type="button"
          onClick={handleSendVerifyCode}
        >
          Send verification code to my mobile phone via SMS
        </button>
        {countdown > 0 && <p className="is-size-7 has-text-danger">Resend in {countdown} seconds</p>}
        <MessageTip statusObj={smsSendStatus} />
        <label className="public-secure-file-label" htmlFor="mfa-verify-code">
          Verification code
        </label>
        <input
          id="mfa-verify-code"
          className="public-secure-file-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={verificationCode}
          onChange={(event) => setVerificationCode(event.target.value)}
        />
        {smsSendStatus.status === SUCCEED && (
          <button
            className={`public-secure-file-button public-secure-file-button--primary ${
              verifyStatus.status === LOADING ? "is-loading" : ""
            }`}
            type="button"
            onClick={handleVerify}
          >
            Verify
          </button>
        )}
        <MessageTip statusObj={verifyStatus} mapping={VERIFY_RESULT_MAPPING} />
      </form>
    </AuthPageLayout>
  );
};

export default MFAVerify;
